/**
 * useIoTAttribute Hook
 *
 * React hook for fetching and caching IoT sensor data
 * Handles loading, error, and caching states
 */

import { useState, useEffect, useCallback } from "react"
import { useApolloClient } from "@apollo/client"
import type { AttributeConfig, ActivityContext } from "../types"
import { useAttributeRuleEngine } from "./useAttributeRuleEngine"
import { AssetBasic, Asset } from "@hydrologiq/gql/graphql"
import { DateTime } from "timezonecomplete"
import { toUserTZ } from "@hydrologiq/common/helpers/date"

/**
 * Options for useIoTAttribute hook
 */
export interface UseIoTAttributeOptions {
  /** Attribute configuration with iotSource */
  config: AttributeConfig

  /** Current activity context */
  context: ActivityContext

  /** Whether to fetch data (default: true) */
  enabled?: boolean

  /** Current asset */
  asset: AssetBasic | undefined
}

/**
 * Result returned by useIoTAttribute hook
 */
export interface UseIoTAttributeResult {
  /** Current attribute value from IoT */
  value: string | null

  /** Whether data is being fetched */
  loading: boolean

  /** Error if fetch failed */
  error: Error | null

  /** Function to manually refetch data */
  refetch: () => Promise<void>
}

/**
 * Hook for fetching IoT attribute values
 *
 * Automatically fetches sensor data when component mounts and context changes
 * Supports caching and manual refresh
 *
 * @param options - Configuration options
 * @returns IoT attribute state and refetch function
 *
 * @example
 * ```tsx
 * const { value, loading, error, refetch } = useIoTAttribute({
 *   config: quantityConfig,
 *   context: activityContext
 * })
 *
 * if (loading) return <Spinner />
 * if (error) return <ErrorMessage />
 * return <Input value={value || ''} />
 * ```
 */
export function useIoTAttribute({
  config,
  context,
  asset,
  enabled = true,
}: UseIoTAttributeOptions): UseIoTAttributeResult {
  const apolloClient = useApolloClient()
  const ruleEngine = useAttributeRuleEngine()

  const [value, setValue] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchValue = useCallback(async () => {
    if (!enabled || !config.iotSource) {
      return
    }

    // Check if asset has active monitoring - if not, skip IoT fetch
    // Get asset from order (assets are now assigned at order level)
    const monitoringStatus = asset?.monitoring_status || (asset as Asset)?.assignments
    if (!monitoringStatus) {
      setValue(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const iotValue = await ruleEngine.fetchIoTValue(
        config,
        context,
        apolloClient,
        asset,
        asset?.monitoring_start ? toUserTZ(new DateTime(asset.monitoring_start)) : undefined
      )
      setValue(iotValue)
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error("IoT fetch failed")
      setError(errorObj)
      setValue(config.iotSource.fallbackValue ?? null)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.iotSource,
    config.attribute,
    asset?.id,
    asset?.monitoring_status,
    context.activity.id,
    context.eventKind,
    enabled,
    apolloClient,
    ruleEngine,
  ])

  // Fetch on mount and when context changes
  useEffect(() => {
    fetchValue()
  }, [fetchValue])

  // No automatic polling - user must manually refresh using refetch button

  return {
    value,
    loading,
    error,
    refetch: fetchValue,
  }
}
